<template>
  <v-alert
    :class="alertClass"
    variant="outlined"
    :color="fontColor"
    :icon="alertIcon"
    :elevation="elevation"
    v-model="visible"
    :width="width"
    closable
    close-label="Close Alert"
    @click:close="emit('close')"
  >
    <v-alert-title :id="`alert-title-${id}`">{{ title }}</v-alert-title>
    <slot name="stepper-content-area" class="alert">
      <p v-if="bodyCopy" :class="`body ${linkLabel ? '' : 'pb-0'}`" v-html="bodyCopy" />
      <EgovLink
        v-if="linkLabel"
        appendIcon="ri-arrow-right-line"
        :disabled="false"
        :href="ctaLink"
        :label="linkLabel"
        :newTab="ctaNewTab"
        size="large"
        variant="standalone"
      />
    </slot>
  </v-alert>
</template>

<script setup>
import { computed, ref } from 'vue'
import EgovLink from './EgovLink.vue'

const props = defineProps({
  id: { type: String, default: '' },
  type: {
    type: String,
    required: true,
    validator: (value) => ['info', 'warning', 'critical', 'success'].includes(value),
  },
  title: { type: String, required: true },
  bodyCopy: { type: String, required: '' },
  ctaLink: { type: String, default: null },
  ctaNewTab: { type: Boolean, default: false },
  width: { type: String, default: null },
  linkLabel: { type: String, default: '' },
  elevation: { type: Number, default: 0, validator: (value) => [0, 1, 2, 3, 4].includes(value) },
})

const emit = defineEmits(['close'])
const visible = ref(true)

const alertClass = computed(() => ({
  'alert-info': props.type === 'info',
  'alert-warning': props.type === 'warning',
  'alert-critical': props.type === 'critical',
  'alert-success': props.type === 'success',
}))

// Font Color
const fontColor = computed(() => {
  switch (props.type) {
    case 'info':
      return 'var(--kds-shadow)'
    case 'warning':
      return 'rgba(41, 24, 0, 1)'
    case 'critical':
      return 'rgba(65, 0, 2, 1)'
    default:
      return 'primary'
  }
})

const alertIcon = computed(() => {
  switch (props.type) {
    case 'info':
      return 'ri-information-line'
    case 'warning':
      return 'ri-alert-line'
    case 'critical':
      return 'ri-error-warning-line'
    default:
      return 'ri-information-line'
  }
})
</script>

<style lang="scss" scoped>
.v-alert {
  border-radius: 4px;
  padding: 20px !important;

  .v-alert-title {
    margin-top: -3px;
    padding-left: 2px; // Space for CTA focus border
  }
}

.body {
  padding-top: 3px;
  padding-bottom: 16px;
}

.alert-info {
  border-color: var(--kds-primary);
  background-color: var(--kds-primary-container);
}

.alert-warning {
  border-color: rgba(131, 85, 0, 1);
  background-color: rgba(255, 221, 180, 1);
}

.alert-critical {
  border-color: rgba(186, 26, 26, 1);
  background-color: rgba(255, 218, 214, 1);
}

.v-alert-title {
  font-size: 1rem;
  font-weight: 700;
}

.alert-success {
  border-color: rgba(46, 125, 50, 1);
  background-color: rgba(232, 245, 233, 1);
}
.alert-content {
  font-size: 0.875rem;
  font-weight: 400;
  padding: 2px; // Space for CTA focus border
  padding-bottom: 3px;
}
</style>
